import React, { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * App-owned on-screen keyboard for the touch till.
 *
 * The POS must not depend on the OS/desktop keyboard (a locked kiosk has none),
 * so this renders in-browser, docks to the bottom, and writes into whatever
 * input/textarea is focused. Values are set via the native setter + an `input`
 * event so React's controlled inputs update correctly.
 *
 * Enabled automatically on touch devices (coarse pointer) or when the kiosk
 * flag is set (`localStorage.kiosk=1` or `?kiosk=1`); invisible on a desktop
 * with a mouse so dev is unaffected.
 */

type Target = HTMLInputElement | HTMLTextAreaElement;

const NON_TEXT_TYPES = new Set(['checkbox', 'radio', 'range', 'color', 'file', 'button', 'submit', 'reset', 'date', 'time', 'datetime-local', 'month', 'week']);

function isEditable(el: Element | null): el is Target {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'TEXTAREA') return true;
  if (tag === 'INPUT') {
    const t = (el as HTMLInputElement).type;
    return !NON_TEXT_TYPES.has(t) && !(el as HTMLInputElement).readOnly && !(el as HTMLInputElement).disabled;
  }
  return false;
}

function wantsNumeric(el: Target): boolean {
  if (el.tagName === 'INPUT') {
    const inp = el as HTMLInputElement;
    if (inp.type === 'number' || inp.type === 'tel') return true;
    const m = (inp.getAttribute('inputmode') || '').toLowerCase();
    if (m === 'numeric' || m === 'decimal' || m === 'tel') return true;
  }
  return false;
}

function setNativeValue(el: Target, value: string) {
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  setter?.call(el, value);
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

function insertAtCursor(el: Target, text: string) {
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? el.value.length;
  setNativeValue(el, el.value.slice(0, start) + text + el.value.slice(end));
  const pos = start + text.length;
  try { el.selectionStart = el.selectionEnd = pos; } catch { /* number inputs disallow selection */ }
}

function backspace(el: Target) {
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? el.value.length;
  if (start === end) {
    if (start === 0) return;
    setNativeValue(el, el.value.slice(0, start - 1) + el.value.slice(end));
    try { el.selectionStart = el.selectionEnd = start - 1; } catch { /* */ }
  } else {
    setNativeValue(el, el.value.slice(0, start) + el.value.slice(end));
    try { el.selectionStart = el.selectionEnd = start; } catch { /* */ }
  }
}

const ROWS_LOWER = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm', '@', '.'],
];
const ROWS_UPPER = ROWS_LOWER.map((r) => r.map((k) => (/[a-z]/.test(k) ? k.toUpperCase() : k)));
const NUM_ROWS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['.', '0', '×'], // × = backspace on numeric
];

export const OnScreenKeyboard: React.FC = () => {
  // Till/kiosk ONLY. A phone or tablet customer on the QR menu has their own
  // native keyboard — we must never show this for them. So gate strictly on an
  // explicit kiosk flag the till sets (?kiosk=1 in the kiosk URL, which also
  // persists to localStorage), NOT on touch/coarse-pointer.
  const enabled = useMemo(() => {
    if (typeof window === 'undefined') return false;
    try {
      if (/[?&]kiosk=1/.test(window.location.search)) {
        localStorage.setItem('kiosk', '1'); // sticky for in-app navigations that drop the query
        return true;
      }
      return localStorage.getItem('kiosk') === '1';
    } catch { return false; }
  }, []);

  const [target, setTarget] = useState<Target | null>(null);
  const [shift, setShift] = useState(false);
  const numeric = target ? wantsNumeric(target) : false;

  useEffect(() => {
    if (!enabled) return;
    const onFocusIn = (e: FocusEvent) => {
      const el = e.target as Element | null;
      if (isEditable(el)) { setTarget(el); setShift(false); }
    };
    const onFocusOut = (e: FocusEvent) => {
      // Only hide if focus left for a non-editable element. A small delay lets a
      // key tap (which we preventDefault anyway) not race the blur.
      const next = (e as FocusEvent & { relatedTarget: Element | null }).relatedTarget;
      window.setTimeout(() => {
        const active = document.activeElement;
        if (!isEditable(active) && !isEditable(next)) setTarget(null);
      }, 60);
    };
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, [enabled]);

  // Keys must not steal focus from the input — preventDefault on pointerdown.
  const press = useCallback((val: string) => {
    if (!target) return;
    if (val === '__HIDE__') { target.blur(); setTarget(null); return; }
    if (val === 'BKSP' || val === '×') return backspace(target);
    if (val === 'SPACE') return insertAtCursor(target, ' ');
    if (val === 'ENTER') {
      const form = target.form;
      if (form) form.requestSubmit?.();
      else target.blur();
      return;
    }
    if (val === 'SHIFT') { setShift((s) => !s); return; }
    insertAtCursor(target, val);
    if (shift) setShift(false);
  }, [target, shift]);

  if (!enabled || !target) return null;

  const rows = numeric ? NUM_ROWS : (shift ? ROWS_UPPER : ROWS_LOWER);

  const Key: React.FC<{ label: React.ReactNode; val: string; grow?: number; accent?: 'action' | 'danger' | 'neutral' }> =
    ({ label, val, grow = 1, accent }) => (
      <button
        type="button"
        onPointerDown={(e) => { e.preventDefault(); press(val); }}
        style={{
          flex: `${grow} 1 0`,
          minWidth: 0,
          height: 52,
          margin: 3,
          borderRadius: 9,
          border: '1px solid rgba(255,255,255,0.12)',
          background: accent === 'action' ? 'var(--action, #A66A10)'
            : accent === 'danger' ? 'rgba(224,106,94,0.22)'
              : 'rgba(255,255,255,0.09)',
          color: accent === 'danger' ? '#F5CEC8' : '#F5EFE6',
          fontSize: 19,
          fontWeight: 600,
          cursor: 'pointer',
          touchAction: 'manipulation',
          userSelect: 'none',
        }}
      >
        {label}
      </button>
    );

  return (
    <div
      role="group"
      aria-label="On-screen keyboard"
      style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 2147483000,
        background: 'linear-gradient(180deg,#241826,#140D16)',
        borderTop: '1px solid rgba(255,255,255,0.12)',
        padding: '6px 8px calc(env(safe-area-inset-bottom, 0px) + 8px)',
        boxShadow: '0 -8px 30px rgba(0,0,0,0.4)',
      }}
    >
      <div style={{ maxWidth: numeric ? 340 : 900, margin: '0 auto' }}>
        {rows.map((row, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'center' }}>
            {i === (numeric ? -1 : 2) && !numeric && (
              <Key label={shift ? '⇧' : '⇧'} val="SHIFT" grow={1.4} accent={shift ? 'action' : 'neutral'} />
            )}
            {row.map((k) => (
              <Key key={k} label={k === '×' ? '⌫' : k} val={k} />
            ))}
            {i === (numeric ? -1 : 3) && !numeric && (
              <Key label={'⌫'} val="BKSP" grow={1.4} accent="danger" />
            )}
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {!numeric && <Key label="space" val="SPACE" grow={5} />}
          {numeric && <Key label={'⌫'} val="BKSP" grow={2} accent="danger" />}
          <Key label="Enter" val="ENTER" grow={2} accent="action" />
          <Key label="Hide ⌄" val="__HIDE__" grow={1.6} accent="neutral" />
        </div>
      </div>
    </div>
  );
};

export default OnScreenKeyboard;
